/**
 * Typed application routes: the single source of URL truth.
 *
 * `routes` is a complete dictionary of the app's pages by name, checked
 * against the route types Next.js generates, so it cannot drift from
 * `src/pages` or from the rewrites in next.config.js. `links` is the curated
 * front door on top of it: shorter, named for the domain, and the thing most
 * call sites should reach for.
 *
 * The builder itself lives in `@/lib/route-builder`.
 */

import type { Vertical } from "@/types/chat"
import type { SettingTabType } from "@/types/settings"
import type { TripTab } from "@/types/trip"

import placePathTypes from "@/lib/place-path-types.json"
import { compareRouteSpecificity, route as r } from "@/lib/route-builder"

// ---------- Route table ----------
//
// Every page under src/pages, sorted by path, plus the rewrite aliases at the
// end. It is a dictionary, not a shortlist: test/lib/routes.spec.ts fails both
// ways — when an entry has no page file, and when a page file has no entry —
// so a new page cannot quietly skip it.
//
// Adding a page: one line, in path order, named after the path with the
// `internal/` prefix dropped (`/internal/agentic-evaluations/[id]` ->
// `agenticEvaluation`). Rename only when the path is not an identifier or
// would collide (see /404, /profile).
// The pattern is checked against the types `next typegen` emits, so if the
// editor says a page you just added does not exist, run `npm run web:typegen`.
//
// `.query<{...}>()` is opt-in: declare it where call sites build a query, and
// unknown keys become type errors for that route. Routes without it accept
// any query.
//
// A param value is one segment. `href()` encodes a slash inside it, so a
// catch-all takes its pieces as separate array elements, never pre-joined.

/** The Bookings page's list state; a default value is left out of the URL. */
export type BookingsQuery = {
  filter?: "assigned" | "unassigned"
  /** "true" lists cancelled bookings too. */
  cancelled?: string
  q?: string
}

type DashboardQuery = {
  tab?: string
  subtab?: string
  view?: string
  chat_id?: string | number
}

export const routes = {
  home: r("/"),
  notFound: r("/404"),
  rateLimited: r("/429"),
  serverError: r("/500"),
  about: r("/about"),
  admin: r("/admin/[[...slug]]"),
  adminLoyaltyDataIssues: r("/admin/loyalty-data-issues"),
  adminLoyaltyPrograms: r("/admin/loyalty-programs"),
  adminPlaceReviews: r("/admin/place-reviews"),
  adminUsers: r("/admin/users"),
  adminTestUsers: r("/admin/test-users"),
  agents: r("/agents"),
  agent: r("/agents/[id]"),
  agentsManage: r("/agents/manage"),
  appLink: r("/app-link/[[...action]]"),
  authBasic: r("/auth/basic"),
  authCliLogin: r("/auth/cli-login"),
  authCreateAnon: r("/auth/create-anon"),
  authDisableV2: r("/auth/disable-v2"),
  authEnableV2: r("/auth/enable-v2"),
  authError: r("/auth/error"),
  authGmailCallback: r("/auth/gmail/callback"),
  authGoogleCalendarCallback: r("/auth/google-calendar/callback"),
  authMcpAuthorize: r("/auth/mcp-authorize"),
  authRefresh: r("/auth/refresh"),
  authResetPassword: r("/auth/reset-password"),
  signIn: r("/auth/sign-in").query<{ callbackUrl?: string }>(),
  authSignOut: r("/auth/sign-out"),
  bookings: r("/bookings").query<BookingsQuery>(),
  business: r("/business"),
  businessDestinations: r("/business/destinations"),
  businessHotels: r("/business/hotels"),
  businessHowItWorks: r("/business/how-it-works"),
  businessPackages: r("/business/packages"),
  calendar: r("/calendar"),
  chat: r("/chat/[[...id]]"),
  checkSettings: r("/check-settings"),
  collection: r("/collection/[[...slug]]"),
  contact: r("/contact"),
  cookiePolicy: r("/cookie-policy"),
  createVanityLink: r("/create-vanity-link").query<{
    kind: "guide" | "magic-link"
    orgid: string
    customerName?: string
  }>(),
  create: r("/create/[[...slug]]"),
  creatorProgram: r("/creator-program"),
  creatorProgramLearn: r("/creator-program-learn"),
  creatorTerms: r("/creator-terms"),
  dashboard: r("/dashboard/[[...orgid]]").query<DashboardQuery>(),
  dx: r("/dx"),
  dxAvatar: r("/dx/avatar"),
  dxButtons: r("/dx/buttons"),
  dxForms: r("/dx/forms"),
  dxIcons: r("/dx/icons"),
  embedded: r("/embedded/[orgid]"),
  eventActor: r("/event-actor/[[...slug]]"),
  extensionAuthorize: r("/extension/authorize"),
  extensionPanel: r("/extension/panel"),
  extensionSearch: r("/extension/search"),
  explore: r("/explore/[[...slug]]").query<{ loc?: string; type?: string }>(),
  featurePreviewCustomAgentSuggestions: r(
    "/feature-preview/custom-agent-suggestions"
  ),
  flights: r("/flights"),
  flightsBooking: r("/flights/booking/[id]"),
  giveAwayTerms: r("/give-away-terms"),
  handoff: r("/handoff/[code]"),
  appHome: r("/home"),
  hotelRooms: r("/hotels/[id]/rooms"),
  hotelsBookingDetails: r("/hotels/booking-details/[id]"),
  hotelsBooking: r("/hotels/booking/[id]"),
  inspiration: r("/inspiration/[[...slug]]").query<{ search?: string }>(),
  agenticEvaluations: r("/internal/agentic-evaluations"),
  agenticEvaluation: r("/internal/agentic-evaluations/[id]").query<{
    callbackUrl?: string
  }>(),
  agenticEvaluationNew: r("/internal/agentic-evaluations/new").query<{
    cloneFrom?: string | number
    callbackUrl?: string
  }>(),
  agenticEvaluationReports: r("/internal/agentic-evaluations/reports"),
  agenticEvaluationReport: r("/internal/agentic-evaluations/reports/[id]"),
  agenticEvaluationRuns: r("/internal/agentic-evaluations/runs").query<{
    run_ids?: readonly string[]
  }>(),
  agenticEvaluationRun: r("/internal/agentic-evaluations/runs/[id]").query<{
    callbackUrl?: string
  }>(),
  agenticPlayground: r("/internal/agentic-playground"),
  agenticPlaygroundSession: r("/internal/agentic-playground/[id]"),
  agenticPlaygroundCustomAgents: r(
    "/internal/agentic-playground/custom-agents"
  ),
  agenticPlaygroundModels: r("/internal/agentic-playground/models"),
  agenticPlaygroundModel: r("/internal/agentic-playground/models/[id]"),
  batchProcessing: r("/internal/batch-processing/[[...slug]]").query<{
    run?: number
  }>(),
  internalChat: r("/internal/chats/[id]").query<{ callbackUrl?: string }>(),
  copyProduction: r("/internal/copy-production"),
  copyProductionPackage: r("/internal/copy-production/packages/[id]"),
  copyProductionWorkflow: r("/internal/copy-production/workflows/[id]").query<{
    duplicate_from?: string | number
  }>(),
  internalEmails: r("/internal/emails/[[...slug]]").query<{
    booking_ref?: string
    recipient?: string
  }>(),
  flightCardParity: r("/internal/flight-card-parity"),
  forYouRibbonLab: r("/internal/for-you-ribbon-lab"),
  hotelQuoteCardGallery: r("/internal/hotel-quote-card-gallery"),
  imagePackLab: r("/internal/image-pack-lab"),
  imagePacks: r("/internal/image-packs"),
  llmReplay: r("/internal/llm-replay").query<{
    messageId?: string | number
  }>(),
  quoteValueLab: r("/internal/quote-value-lab"),
  suggestionLab: r("/internal/suggestion-lab"),
  toastDemo: r("/internal/toast-demo"),
  voiceHarness: r("/internal/voice-harness"),
  voiceUiGallery: r("/internal/voice-ui-gallery"),
  webEventCardGallery: r("/internal/web-event-card-gallery"),
  iosHandoff: r("/ios/handoff/[code]"),
  joinCollection: r("/join-collection/[token]"),
  joinCreatorProgram: r("/join-creator-program"),
  joinOrganization: r("/join-organization/[code]"),
  joinTrip: r("/join-trip/[code]"),
  legalDpa: r("/legal/dpa"),
  legalSubProcessors: r("/legal/sub-processors"),
  mediakit: r("/mediakit"),
  partnersBookwithtuiPlanner: r("/partners/bookwithtui/planner"),
  pendingMigrations: r("/pending-migrations"),
  place: r("/place/[[...slug]]"),
  press: r("/press"),
  pressPost: r("/press/[slug]"),
  privacyPolicy: r("/privacy-policy"),
  proactiveAgentsUnsubscribe: r("/proactive-agents/unsubscribe"),
  myProfile: r("/profile"),
  profile: r("/profile/[handle]").query<{ tab?: "guides"; edit?: "1" }>(),
  profileByRef: r("/profile/by-ref/[ref]"),
  question: r("/question/[place_slug]/[url_slug]/[question_ref]"),
  receipt: r("/receipt/[folioDocumentRef]"),
  saved: r("/saved/[[...slug]]"),
  settings: r("/settings").query<{
    section?: SettingTabType
    mem?: string
    gmail?: "connected" | "failed"
  }>(),
  share: r("/share/[id]").query<{ type?: "inspiration" }>(),
  shortUrl: r("/short-url/[code]"),
  stays: r("/stays"),
  stories: r("/stories"),
  team: r("/team"),
  termsOfService: r("/terms-of-service"),
  trip: r("/trip/[[...slug]]").query<{ ref?: string; tripTab?: TripTab }>(),
  trips: r("/trips"),
  unsubscribe: r("/unsubscribe"),
  updates: r("/updates"),
  vanity: r("/vanity/[id]"),
  verifyEmail: r("/verify/email/[id]"),
  welcome: r("/welcome"),
  you: r("/you"),
  // Public aliases rewritten in next.config.js; the target page is above.
  playground: r("/playground/[[...path]]"),
} as const

// ---------- Matching ----------

/**
 * The first segment of a canonical place URL (`/hotel/...`). next.config.js
 * rewrites each to the place page from the same list.
 */
export const PLACE_PATH_TYPES: readonly string[] = placePathTypes

export type RouteName = keyof typeof routes

type RouteParams<N extends RouteName> = NonNullable<
  ReturnType<(typeof routes)[N]["match"]>
>

/** A matched route, discriminated on `name` so its params are typed. */
export type RouteMatch = {
  [N in RouteName]: { readonly name: N; readonly params: RouteParams<N> }
}[RouteName]

function isRouteName(key: string): key is RouteName {
  return key in routes
}

const routesBySpecificity = Object.keys(routes)
  .filter(isRouteName)
  .sort((a, b) => compareRouteSpecificity(routes[a].pattern, routes[b].pattern))

/**
 * Applies the place rewrites, which `routes` cannot express as a pattern.
 * This is also the path the router fetches page data for.
 */
export function rewritePath(path: string): string {
  const [, first] = path.split(/[/?]/)
  return PLACE_PATH_TYPES.includes(first)
    ? path.replace(`/${first}`, "/place")
    : path
}

/**
 * The page a path renders, as Next would resolve it, with its params parsed.
 * Null for a path no page serves.
 *
 *   matchRoute("/trip/abc?tripTab=bookings")
 *   // { name: "trip", params: { slug: ["abc"], query: { tripTab: "bookings" } } }
 */
export function matchRoute(path: string): RouteMatch | null
// TypeScript cannot correlate `name` with `params` across the loop; the
// signature above states the pairing each iteration upholds.
export function matchRoute(path: string) {
  const resolved = rewritePath(path)
  for (const name of routesBySpecificity) {
    const params = routes[name].match(resolved)
    if (params) return { name, params }
  }
  return null
}

// ---------- Semantic helpers ----------
//
// Where a param has domain meaning, name it. Add a helper here when a route
// is linked from several places or its params carry meaning the route name
// does not (`links.trip(ref, "bookings")`); otherwise `routes.x.href()` at
// the call site is fine. Both are plain strings, so they drop into
// `<Link href>`, `router.push` and `mtLocation` unchanged.

export const links = {
  /** `/chat` or `/chat/{id}`. */
  chat: (id?: string | number) =>
    routes.chat.href(id === undefined ? {} : { id: [String(id)] }),
  /** With a tab, opens the trip sheet on it (`?ref=` is the sheet locator). */
  trip: (ref: string, tab?: TripTab) =>
    routes.trip.href({
      slug: [ref],
      query: tab ? { ref, tripTab: tab } : undefined,
    }),
  /** Landing page of a vertical (`/flights`, `/stays`). */
  vertical: (vertical: Vertical) =>
    ({ flights: routes.flights, stays: routes.stays })[vertical].href(),
  profile: (handle: string, tab?: "guides") =>
    routes.profile.href({ handle, query: { tab } }),
  profileByRef: (ref: string) => routes.profileByRef.href({ ref }),
  inspiration: (inspiration: { canonical_name: string; entity_ref: string }) =>
    routes.inspiration.href({
      slug: [inspiration.canonical_name, inspiration.entity_ref],
    }),
  flightBooking: (id: string | number) =>
    routes.flightsBooking.href({ id: String(id) }),
  hotelBooking: (ref: string) => routes.hotelsBooking.href({ id: ref }),
  hotelBookingDetails: (ref: string) =>
    routes.hotelsBookingDetails.href({ id: ref }),
  receipt: (folioDocumentRef: string) =>
    routes.receipt.href({ folioDocumentRef }),
  joinTrip: (code: string) => routes.joinTrip.href({ code }),
  joinCollection: (token: string) => routes.joinCollection.href({ token }),
  dashboard: (orgid: string, query?: DashboardQuery) =>
    routes.dashboard.href({ orgid: [orgid], query }),
  signIn: (callbackUrl: string) =>
    routes.signIn.href({ query: { callbackUrl } }),
  settings: (section?: SettingTabType) =>
    routes.settings.href({ query: { section: section || undefined } }),
  playgroundSession: (id: string | number) =>
    routes.playground.href({ path: [String(id)] }),
}
